import { Col, Input, InputNumber, Row, Space, Table } from "antd";
import { useEffect, useState } from "react";
import { BaselClassificationModel } from "../../../../../../models/scenario-frontier.model";
import { AMOUNT_UTIL } from "../../../../../../utils/amount.utils";
import i18n from "../../../../../../utils/i18n";
import { DoubleArrowIcon } from "../../../../../atoms/icon";
import PercentInput from "../../../../../atoms/percent-input";

interface Props {
    data: BaselClassificationModel[];
    loading: boolean;
    update: (item: BaselClassificationModel) => void
}
const BaselClassifications = (props: Props) => {

    const [editRows, setEditRows] = useState<BaselClassificationModel[]>([]);
    const [data, setData] = useState<BaselClassificationModel[]>([]);

    const columns: any[] = [
        {
            title: i18n.t("ScenarioFrontier.lbAssetClass"),
            dataIndex: 'name',
            key: 'name',
            render: (p: string, record: BaselClassificationModel) => renderInputCell(record.id, p, 'name')
        },
        {
            title: i18n.t("ScenarioFrontier.lbRhoBottom"),
            dataIndex: 'rho_bottom',
            key: 'rho_bottom',
            align: "right",
            render: (p: number, record: BaselClassificationModel) => renderPercentInputCell(record.id, p, 'rho_bottom')
        },
        {
            title: i18n.t("ScenarioFrontier.lbRhoTop"),
            dataIndex: 'rho_top',
            key: 'rho_top',
            align: "right",
            render: (p: number, record: BaselClassificationModel) => renderPercentInputCell(record.id, p, 'rho_top')
        }, {
            title: i18n.t("ScenarioFrontier.lbRhoMultiplier"),
            dataIndex: 'rho_multiplier',
            key: 'rho_multiplier',
            align: "right",
            render: (p: number, record: BaselClassificationModel) => renderNumberInputCell(record.id, p, 'rho_multiplier')
        },
        {
            title: i18n.t("ScenarioFrontier.lbCyclicality"),
            dataIndex: 'cyclicality',
            key: 'cyclicality',
            align: "right",
            render: (p: number, record: BaselClassificationModel) => renderPercentInputCell(record.id, p, 'cyclicality')
        },
        {
            title: i18n.t("ScenarioFrontier.lbCalibration"),
            dataIndex: 'calibration',
            key: 'calibration',
            align: "right",
            render: (p: number, record: BaselClassificationModel) => renderPercentInputCell(record.id, p, 'calibration')
        },
        {
            title: i18n.t("ScenarioFrontier.lbRWAMultiplier"),
            dataIndex: 'rwa_multiplier',
            key: 'rwa_multiplier',
            align: "right",
            render: (p: number, record: BaselClassificationModel) => renderNumberInputCell(record.id, p, 'rwa_multiplier')
        },
        {
            title: i18n.t("ScenarioFrontier.lbActions"),
            dataIndex: 'actions',
            key: 'actions',
            render: (text: string, record: BaselClassificationModel) => <>
                <Space>
                    {renderActions(record.id)}
                </Space>
            </>
        }
    ]

    const editRow = (id: number) => {
        setEditRows(pre => {
            const item = props.data.find(x => x.id === id);
            if (item) {
                pre.push({ ...item });
            }
            return [...pre];
        });
    }

    const saveRow = (id: number) => {

        const rowItem = data.find(x => x.id === id);

        if (rowItem) {
            props.update(rowItem);
        }

        setEditRows(pre => {
            pre = pre.filter(x => x.id !== id);
            return [...pre];
        });
    }

    const cancel = (id: number) => {

        setData(pre => {
            const item = editRows.find(x => x.id === id);
            const index = pre.findIndex(x => x.id === id);

            if (item) {
                pre[index] = item;
            }

            return [...pre]
        });

        setEditRows(pre => {
            pre = pre.filter(x => x.id !== id);
            return [...pre];
        });
    }

    const inputChange = (value: any, id: number, name: string) => {
        setData(pre => {
            const item: any = pre.find(x => x.id === id);
            if (item) {
                item[name] = value;
            }

            return [...pre]
        })
    }

    const renderInputCell = (id: number, value: string, name: string) => {
        if (editRows.some(x => x.id === id)) {
            return <Input onChange={(e) => inputChange(e.target.value, id, name)}
                value={value} />
        } else {
            return <> {value}
            </>
        }
    }

    const renderPercentInputCell = (id: number, value: number, name: string) => {
        if (editRows.some(x => x.id === id)) {
            return <PercentInput onChange={(value) => inputChange(value, id, name)}
                value={value} />
        } else {
            return <> {AMOUNT_UTIL.converNumberToPercentage(value)}
            </>
        }
    }

    const renderNumberInputCell = (id: number, value: number, name: string) => {
        if (editRows.some(x => x.id === id)) {
            return <InputNumber onChange={(value) => inputChange(value, id, name)}
                value={value} />
        } else {
            return <> {AMOUNT_UTIL.convertNumericToFormattedString(value)}
            </>
        }
    }

    const renderActions = (id: number) => {

        if (editRows.some(x => x.id === id)) {
            return <>
                <span className="blue-text action-text" onClick={() => saveRow(id)}>
                    {i18n.t("ScenarioFrontier.lbSave")}
                </span>
                <span className="blue-text action-text" onClick={() => cancel(id)}>
                    {i18n.t("ScenarioFrontier.lbCancel")}
                </span>
            </>
        } else {
            return <span className="blue-text action-text" onClick={() => editRow(id)}>
                {i18n.t("ScenarioFrontier.lbEdit")}
            </span>
        }
    }

    useEffect(() => {
        if (props.data && !data.length) {
            setData(props.data)
        }
    }, [props.data])

    return (
        <Row>
            <Col span={24}>
                <p className="sub-title rho-title">
                    <DoubleArrowIcon className="svg-icon" />
                    {i18n.t("ScenarioFrontier.lbBaselClassification")}
                    {/* 
                            <span className="blue-text" onClick={() => { setShowRHOTable(false) }}>
                                {i18n.t("ScenarioFrontier.lbHide")}
                            </span> */}
                </p>
                <Table
                    className="second-table"
                    bordered
                    pagination={false}
                    loading={props.loading}
                    dataSource={data}
                    columns={columns} rowKey="id" />
            </Col>
        </Row>
    )
}

export default BaselClassifications;